import { BlogPost } from '../types';

const post: BlogPost = {
	slug: 'cadeaux-pour-grand-frere-grande-soeur',
	title: 'Cadeaux pour un nouveau grand frère ou une nouvelle grande sœur (qui aident vraiment)',
	description:
		'Que donner à l’aîné à l’arrivée d’un nouveau bébé, pourquoi un cadeau « grand frère / grande sœur » compte plus qu’il n’y paraît, et comment bien choisir le moment.',
	date: '2026-03-11',
	heroImage: '/assets/hero/kid-2/classic_scene.png',
	tags: ['idées cadeaux', 'nouveau bébé'],
	lang: 'fr',
	dateLocale: 'fr-FR',
	body: `Quand un nouveau bébé arrive, l’aîné hérite d’un drôle de marché. La maison se remplit de visiteurs, et presque toute l’attention va à quelqu’un qui ne tient pas encore sa tête tout seul.

Un cadeau pour l’aîné à ce moment-là n’a pas vraiment à voir avec l’objet. Il s’agit de lui confier un rôle.

## Pourquoi un cadeau « grand frère / grande sœur » mérite d’être préparé

Les enfants entre deux et six ans comprennent généralement ce qui se passe, sans avoir la moindre stratégie pour y faire face. Ils ne sont pas réellement supplantés, mais ce n’est pas une lecture déraisonnable des indices qu’ils ont sous les yeux.

Offrir à l’aîné quelque chose qui le désigne — *grand frère*, *grande sœur* — recadre l’événement. Il n’est pas celui à qui on retire quelque chose ; il est celui qu’on vient de promouvoir.

Cela fonctionne mieux quand le cadeau est spécifique plutôt que générique. Un t-shirt « Grand Frère » fait l’affaire. Un livre où l’aîné est le héros illustré, et où le bébé est un personnage secondaire dans *son* histoire à lui, fait bien davantage.

## Quoi offrir

**Un livre personnalisé qui leur donne le rôle du grand frère ou de la grande sœur.** L’option la plus forte. Elle donne à l’enfant un récit dans lequel il a déjà un rôle, et ce récit se relit — ce qui compte, car ce sentiment ne se résout pas en un seul après-midi.

**Un livre de coloriage personnalisé.** Utile pour le problème très particulier des premières semaines : de longues plages où un parent est occupé, et où l’aîné a besoin de quelque chose d’absorbant qui ne demande pas de surveillance.

**Un souvenir réunissant les deux enfants.** C’est davantage un cadeau pour les parents, mais il traverse les décennies.

**Quelque chose qui n’appartient qu’à lui.** Un mug, un tote bag, un t-shirt avec son propre personnage. L’idée : le bébé n’a aucun droit dessus. Les enfants de trois ans prennent ça très au sérieux.

## Ce qu’il faut éviter

- **Tout ce que le bébé finira par hériter.** Cela sape entièrement le message.
- **Les cadeaux présentés comme venant « du bébé ».** Les aînés voient clair dans ce stratagème immédiatement, et cela a tendance à passer pour légèrement insultant.
- **Les jouets qui nécessitent un parent pour fonctionner.** La disponibilité des parents, dans les premières semaines, est la ressource la plus rare de la maison.

## Le bon moment

Remettez-le le jour où le bébé rentre à la maison, ou lors de la première visite à la maternité. C’est le moment où l’aîné perçoit le plus intensément le changement.

Cela suppose de commander à l’avance. Les articles personnalisés sont fabriqués à la commande — génération des illustrations, votre validation, impression, expédition — donc comptez deux à trois semaines. Les dates prévues d’accouchement étant approximatives, commander un mois à l’avance est raisonnable.

Si vous êtes un proche en visite plutôt qu’un parent : apportez quelque chose pour l’aîné. Tout le monde apportera quelque chose pour le bébé, et l’aîné, lui, comptera.

## Une remarque sur le deuxième enfant

Si vous faites cela pour le premier enfant, prévoyez de le refaire pour le second, quelques années plus tard. Les enfants tiennent les comptes sur ce sujet avec une précision remarquable, et « il y a un livre sur toi et pas sur moi » est un grief qui se conserve très longtemps.`,
	faqs: [
		{
			question: 'Que faut-il offrir à l’aîné quand un nouveau bébé arrive ?',
			answer:
				'Quelque chose qui lui appartient sans ambiguïté et qui nomme son nouveau rôle. Un livre personnalisé qui présente l’aîné comme le grand frère ou la grande sœur fonctionne particulièrement bien, car il lui donne un rôle narratif plutôt qu’une simple étiquette, et il peut être relu au fil des semaines qui suivent.',
		},
		{
			question: 'Quand offrir un cadeau de grand frère ou grande sœur ?',
			answer:
				'Le jour où le bébé rentre à la maison, ou lors de la première visite à la maternité — le moment où l’aîné perçoit le plus le changement. Les articles personnalisés étant fabriqués à la commande, prévoyez de commander environ un mois à l’avance, les dates d’accouchement étant approximatives.',
		},
		{
			question: 'Faut-il présenter le cadeau de grand frère ou grande sœur comme venant du bébé ?',
			answer:
				'En général, non. Les aînés voient généralement clair dans le stratagème, et cela peut être perçu comme condescendant. C’est plus efficace de le présenter simplement comme venant de l’adulte qui l’offre.',
		},
	],
	ui: {
		homeLabel: 'Accueil',
		blogLabel: 'Blog',
		faqHeading: 'Questions fréquentes',
		moreReadingHeading: 'À lire aussi',
		ctaLabel: 'Découvrir les cadeaux personnalisés',
	},
};

export default post;
